<template>
  <div>
    <BotCreateModal
      :show="showBotCreate"
      @close="handleCloseBotCreateModal"
      @createBot="handleShowBotCreateAttention"
    />
    <BotCreateAttention
      :show="showBotCreateAttention"
      :loading="loading"
      @close="handleCloseBotCreateAttention"
      @createBot="handleCreateBot"
    />
  </div>
</template>

<script setup lang="ts">
import BotCreateModal from "@/modules/Bots/components/BotCreateModal.vue";
import BotCreateAttention from "@/modules/Bots/components/BotCreateAttention.vue";
import { ref } from "vue";
import ApiService from "@/services/ApiService";
import { useI18n } from "vue-i18n";
import { useCustomToast } from "@/composables/useCustomToast";
import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
import { trackCustomEvent } from "@/utils/fbq";

interface Props {
  showBotCreate: boolean;
}

// Define BotCreationData interface
interface BotCreationData {
  exchange: number;
  assigned_money: number;
  [key: string]: any; // Allow for additional properties
}

defineProps<Props>();
const emit = defineEmits<{
  (e: "closeBotCreateModal"): void;
  (e: "close"): void;
  (e: "created"): void;
}>();

const { t } = useI18n();
const { showToast } = useCustomToast();
const { pushToDataLayer } = usePushToDataLayer();

const botCreatingData = ref<BotCreationData | null>(null);

const showBotCreateAttention = ref(false);
const loading = ref(false);

const handleCloseBotCreateModal = () => {
  emit("closeBotCreateModal");
};

const handleShowBotCreateAttention = (data: {
  exchange: number;
  assigned_money: number;
}) => {
  botCreatingData.value = data;
  showBotCreateAttention.value = true;
};

const handleCloseBotCreateAttention = () => {
  showBotCreateAttention.value = false;
};

const handleCreateBot = async () => {
  if (!botCreatingData.value) {
    console.error("Bot creating data is undefined");
    return;
  }

  // Track bot creation attempt
  pushToDataLayer("bot_create_attempt", {
    exchange_id: botCreatingData.value.exchange,
    assigned_money: botCreatingData.value.assigned_money,
    event_category: "bots",
    event_label: "bot_create_attempt",
    button_text: "Create Bot",
  });
  trackCustomEvent("UserCreateBotAttempt", {
    event_category: "create_bot",
    event_label: "create_bot_attempt",
    button_text: "Create Bot",
  });

  loading.value = true;
  try {
    await ApiService.post("/bot/add/automatic/", botCreatingData.value);
    handleCloseBotCreateModal();
    handleCloseBotCreateAttention();
    emit("created");
    showToast(t("success"), "success");

    // Track successful bot creation
    pushToDataLayer("bot_create_success", {
      exchange_id: botCreatingData.value.exchange,
      assigned_money: botCreatingData.value.assigned_money,
      event_category: "bots",
      event_label: "bot_create_success",
      button_text: "Create Bot",
    });
    trackCustomEvent("UserCreateBotSuccess", {
      event_category: "create_bot",
      event_label: "create_bot_success",
      button_text: "Create Bot",
    });
  } catch (err: any) {
    console.error("Failed to create bot:", err);

    // Track failed bot creation with enhanced error details
    pushToDataLayer("bot_create_error", {
      exchange_id: botCreatingData.value.exchange,
      assigned_money: botCreatingData.value.assigned_money,
      error_message: err?.response?.data?.error || "Unknown error",
      status_code: err?.response?.status,
      error_type:
        err?.response?.status === 400
          ? "validation_error"
          : err?.response?.status === 401
            ? "unauthorized"
            : err?.response?.status === 403
              ? "forbidden"
              : err?.response?.status === 404
                ? "not_found"
                : err?.response?.status === 500
                  ? "server_error"
                  : "unknown_error",
      event_category: "bots",
      event_label: "bot_create_error",
      button_text: "Create Bot",
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped></style>
